import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable linebreak-style */
import { useCallback, useState } from 'react';
import { Alert, Breadcrumbs, Button, ButtonContainer, Card, Divider, GridColumn, GridItem, Icon, Link, Spacer, TextField, Tooltip, Typography, useSnackbar, useTheme } from '@emburse/embark-core';
import { HelpIcon } from '@emburse/embark-icons';
import { debounce } from 'lodash';
import { Checkbox } from '../../components';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import { getEmailAddresses, sendEmail } from '../../api/email';
import { $t, useTranslation } from '../../hooks/useTranslation';
import useLoadingOverlay from '../../hooks/useLoadingOverlay';
import { isEmailValid } from '../../util/formValidation';
var Step;
(function (Step) {
    Step[Step["One"] = 1] = "One";
    Step[Step["Two"] = 2] = "Two";
    Step[Step["Three"] = 3] = "Three";
    Step[Step["Four"] = 4] = "Four";
})(Step || (Step = {}));
function EmailUsers() {
    useTranslation();
    const theme = useTheme();
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const { enqueueSnackbar } = useSnackbar();
    const [step, setStep] = useState(1);
    const [administrators, setAdministrators] = useState(false);
    const [accountants, setAccountants] = useState(false);
    const [executives, setExecutives] = useState(false);
    const [managers, setManagers] = useState(false);
    const [employees, setEmployees] = useState(false);
    const [recipients, setRecipients] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [validation, setValidation] = useState('');
    const [fetchedEmails, setFetchedEmails] = useState('');
    const [emailSuccess, setEmailSuccess] = useState('');
    const snackbarHandler = useCallback((message, severity) => {
        enqueueSnackbar(message, { variant: 'alert', AlertProps: { severity } });
    }, [enqueueSnackbar]);
    const updateRecipients = debounce((e) => {
        setValidation('');
        setRecipients(e.target.value);
    }, 50);
    const updateSubject = debounce((e) => setSubject(e.target.value), 50);
    const updateBody = debounce((e) => setBody(e.target.value), 50);
    const isNextDisabled = () => ({
        [Step.One]: administrators || accountants || executives || managers || employees,
        [Step.Two]: !!subject && !!body,
        [Step.Three]: true
    })[step];
    const subHeadText = () => ({
        [Step.One]: $t('Select the groups of users that should receive this email.'),
        [Step.Two]: $t('Enter the message that will be sent to users.'),
        [Step.Three]: $t('Please confirm the content of the email.'),
        [Step.Four]: ''
    })[step];
    const allRecipients = () => {
        if (recipients.length) {
            const cleanedRecipients = recipients.trim().split(';').join('; ');
            if (cleanedRecipients.trim().endsWith(';')) {
                return `${cleanedRecipients} ${fetchedEmails}`;
            }
            return `${cleanedRecipients}; ${fetchedEmails}`;
        }
        return fetchedEmails;
    };
    const visibleRecipients = () => {
        const totalVisible = 125;
        const emailsArray = allRecipients().trim().split(';');
        const total = emailsArray.length;
        if (total > totalVisible) {
            return `${emailsArray.slice(0, totalVisible).join('; ')} + ${total - totalVisible} more`;
        }
        return allRecipients();
    };
    const isRecipientsValid = () => {
        if (recipients.length) {
            const emailArray = recipients.trim().split(';');
            return emailArray.every((email) => {
                if (email.length) {
                    return isEmailValid(email.trim());
                }
                return true;
            });
        }
        return true;
    };
    const getAddresses = () => {
        applyLoadingState(30000);
        const groups = {
            includeAdmins: administrators,
            includeAccountants: accountants,
            includeExecs: executives,
            includeManagers: managers,
            includeEmps: employees
        };
        getEmailAddresses(groups)
            .then((result) => {
            const data = JSON.parse(JSON.stringify(result));
            setFetchedEmails(data);
            setStep(3);
        })
            .catch((err) => snackbarHandler(err.message, 'error'))
            .finally(() => removeLoadingState());
    };
    const sendTheEmail = () => {
        applyLoadingState(30000);
        const email = {
            strToAddresses: allRecipients(),
            strSubject: subject,
            strBody: body
        };
        sendEmail(email)
            .then((result) => {
            setStep(4);
            let successMessage = 'Your email has been sent successfully.';
            if (result) {
                const message = JSON.parse(JSON.stringify(result));
                snackbarHandler(message, 'success');
                successMessage = 'Your emails have been sent successfully.';
            }
            setEmailSuccess(successMessage);
        })
            .catch((err) => snackbarHandler(err.message, 'error'))
            .finally(() => removeLoadingState());
    };
    const nextClickHandler = () => {
        switch (step) {
            case Step.One:
                setStep(2);
                break;
            case Step.Two:
                if (!isRecipientsValid()) {
                    setValidation(`There is a problem with an email address. Please make sure the address is correct. 
                    Multiple addresses must be separated by semicolons`);
                    break;
                }
                getAddresses();
                break;
            case Step.Three:
                sendTheEmail();
                break;
            default:
                break;
        }
    };
    const checkboxes = [
        {
            id: 'Administrators',
            value: administrators,
            action: setAdministrators
        },
        {
            id: 'Accountants',
            value: accountants,
            action: setAccountants
        },
        {
            id: 'Executives',
            value: executives,
            action: setExecutives
        },
        {
            id: 'Managers',
            value: managers,
            action: setManagers
        },
        {
            id: 'Employees',
            value: employees,
            action: setEmployees
        }
    ];
    const tooltip = $t(`To add recipients to this email, type the desired addresses in the text field, separating each address with a semicolon.
                    Example: address1@email.com; address2@email.com; address3@email.com`);
    return (_jsxs(SingleColumnLayout, { children: [step === Step.Four && (_jsxs(_Fragment, { children: [_jsxs(Breadcrumbs, { children: [_jsx(Link, { href: "/CorpConfig.aspx", color: "textSecondary", underline: "none", variant: "body2", children: $t('Configuration') }), _jsx(Typography, { variant: "body2", style: { marginBottom: '-2px' }, children: $t('Send Email to Users') })] }), _jsx(Spacer, { size: 30 })] })), _jsx(Typography, { variant: "h4", children: $t('Email Users') }), _jsx(Typography, { children: subHeadText() }), _jsx(Spacer, { size: 30 }), {
                [Step.One]: (_jsxs(_Fragment, { children: [_jsx(Typography, { children: _jsx("strong", { children: $t('User Groups') }) }), _jsx(GridColumn, { spacing: 1, children: checkboxes.map(({ id, value, action }) => (_jsx(GridItem, { children: _jsx(Checkbox, { id: id, color: "primary", label: $t(id), value: value, checked: value, onChange: (e) => action(e.target.checked) }) }, id))) })] })),
                [Step.Two]: (_jsxs(GridColumn, { spacing: 3, style: { maxWidth: '500px' }, children: [_jsxs(GridItem, { children: [_jsx(TextField, { id: "Recipients", name: "Recipients", label: $t('Recipients'), defaultValue: recipients, helperText: validation, error: !!validation, onChange: updateRecipients, style: { width: '75%' } }), _jsx(Tooltip, { title: tooltip, placement: "right-start", style: { margin: '5px' }, children: _jsx(Icon, { iconUrl: HelpIcon, sx: { color: theme.palette.monochrome.main } }) })] }), _jsx(GridItem, { children: _jsx(TextField, { id: "Subject Line", name: "Subject Line", label: $t('Subject Line'), defaultValue: subject, onChange: updateSubject, style: { width: '75%' } }) }), _jsx(GridItem, { children: _jsx(TextField, { id: "Email Content", name: "Email Content", label: $t('Email Content'), defaultValue: body, multiline: true, fullWidth: true, rows: 4, onChange: updateBody }) })] })),
                [Step.Three]: (_jsx(Card, { variant: "outlined", style: { maxWidth: '600px', borderRadius: '.5rem' }, children: _jsxs(GridColumn, { spacing: 3, children: [_jsxs(GridItem, { children: [_jsx(Typography, { children: _jsx("strong", { children: "Recipients" }) }), _jsx(Typography, { children: visibleRecipients() })] }), _jsxs(GridItem, { children: [_jsx(Typography, { children: _jsx("strong", { children: "Subject Line" }) }), _jsx(Typography, { children: subject })] }), _jsxs(GridItem, { children: [_jsx(Typography, { children: _jsx("strong", { children: "Body" }) }), _jsx(Typography, { children: "(Do not reply to this message as this mailbox is not monitored.)" }), _jsx(Spacer, { size: 20 }), _jsx(Typography, { children: _jsx("div", { dangerouslySetInnerHTML: {
                                                __html: body.replace(/\n/g, '<br />')
                                            } }) })] })] }) })),
                [Step.Four]: (_jsx(Alert, { severity: "success", sx: { width: 'fit-content' }, children: $t(emailSuccess) }))
            }[step], step !== Step.Four && (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 50 }), _jsx(Divider, { orientation: "horizontal", variant: "fullWidth", style: { width: '100%' }, light: true }), _jsx(Spacer, { size: 15 }), _jsxs(ButtonContainer, { children: [step !== Step.One && (_jsx(Button, { variant: "outlined", onClick: () => setStep((prevState) => prevState - 1), children: $t('Back') })), _jsx(Button, { onClick: nextClickHandler, disabled: !isNextDisabled(), children: step === Step.Three ? $t('Send') : $t('Next') })] })] }))] }));
}
export default EmailUsers;
